import { type Socket } from 'socket.io-client';

import type { AugmentedError, SocketErrorPayload } from '@/types';

import rollbar from '@/lib/rollbar';

type SubscriptionCallback<T> = (data: T | null, error: Error | null) => void;

interface SubscriptionInfo {
  apiName: string;
  params: unknown;
  callbacks: Set<SubscriptionCallback<unknown>>;
  dataListener: (response: unknown) => void;
  errorListener: (err: SocketErrorPayload) => void;
}

const activeSubscriptions = new Map<string, SubscriptionInfo>();

const alwaysOpenAPIs: string[] = [];

const getSubscriptionKey = (apiName: string, params: unknown) =>
  `${apiName}:${JSON.stringify(params)}`;

export const GlobalSubscriptionManager = {
  subscribe: <T>(
    socket: Socket,
    apiName: string,
    params: unknown,
    callback: SubscriptionCallback<T>
  ) => {
    const key = getSubscriptionKey(apiName, params);
    let subInfo = activeSubscriptions.get(key);

    if (!subInfo) {
      const dataEvent = `update:${apiName}`;
      const errorEvent = `error:${apiName}`;

      const handleData = (response: unknown) => {
        const currentSub = activeSubscriptions.get(key);

        if (currentSub) {
          currentSub.callbacks.forEach((cb) => cb(response, null));
        }
      };

      const handleError = (err: SocketErrorPayload) => {
        const message = err?.error?.detail || err?.message || 'Unknown socket error';
        const errorObj = new Error(message) as AugmentedError;
        errorObj.meta = err?.error;

        const currentSub = activeSubscriptions.get(key);

        if (currentSub) {
          if (message === 'Invalid request') {
            rollbar.warning(`Socket Validation Error: ${apiName}`, errorObj, {
              params: subInfo?.params || params,
              validationErrors: err?.error?.errors,
            });
          } else {
            rollbar.error(`Socket Subscription Failed: ${apiName}`, errorObj, {
              params: subInfo?.params || params,
            });
          }

          currentSub.callbacks.forEach((cb) => cb(null, errorObj));
        }
      };

      subInfo = {
        apiName,
        params,
        callbacks: new Set(),
        dataListener: handleData,
        errorListener: handleError,
      };
      activeSubscriptions.set(key, subInfo);

      socket.on(dataEvent, handleData);
      socket.on(errorEvent, handleError);

      socket.emit(`subscribe:${apiName}`, params);
    }

    subInfo.callbacks.add(callback as SubscriptionCallback<unknown>);

    if (!socket.hasListeners('connect')) {
      socket.on('connect', () => {
        activeSubscriptions.forEach(({ apiName, params }) => {
          socket.emit(`subscribe:${apiName}`, params);
        });
      });
    }
  },

  unsubscribe: <T>(
    socket: Socket,
    apiName: string,
    params: unknown,
    callback: SubscriptionCallback<T>
  ) => {
    const key = getSubscriptionKey(apiName, params);
    const subInfo = activeSubscriptions.get(key);

    if (!subInfo) {
      return;
    }

    subInfo.callbacks.delete(callback as SubscriptionCallback<unknown>);

    if (subInfo.callbacks.size === 0 && !alwaysOpenAPIs.includes(apiName)) {
      socket.emit(`unsubscribe:${apiName}`, params);
      socket.off(`update:${apiName}`, subInfo.dataListener);
      socket.off(`error:${apiName}`, subInfo.errorListener);
      activeSubscriptions.delete(key);
      GlobalCache.delete(key);
    }
  },
};

const socketDataCache = new Map<string, unknown>();

export const GlobalCache = {
  get: <T>(key: string): T | null => {
    return (socketDataCache.get(key) as T) ?? null;
  },

  set: <T>(key: string, value: T): void => {
    socketDataCache.set(key, value);
  },

  has: (key: string): boolean => {
    return socketDataCache.has(key);
  },

  delete: (key: string): void => {
    socketDataCache.delete(key);
  },
};
